import React, { useState, useEffect, useRef } from 'react';
import {
  Receipt, Plus, Filter, Eye, Pencil, Trash2,
  X, Upload, IndianRupee, FileText, Image,
  ChevronLeft, ChevronRight, RefreshCw
} from 'lucide-react';
import { LoadingSpinner, Button } from '../../UI';
import {
  C, FONT_UI, FONT_DATA, Mono, Card, PageHeader, PrimaryButton, GhostButton, IconButton,
  Toolbar, ChipTabs, StatCard, SectionCard, Donut, Table, TH, TD, EmptyState, Banner, fmtNum,
} from './theme';
import { useApi, useAsyncAction } from '../../../hooks';
import { expenditureAPI, clientAPI } from '../../../services';
import { useAuth } from '../../../context/AuthContext';

const CATEGORIES = [
  { value: 'office_supplies', label: 'Office Supplies' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'rent', label: 'Rent' },
  { value: 'salaries', label: 'Salaries' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'travel', label: 'Travel' },
  { value: 'software', label: 'Software' },
  { value: 'hardware', label: 'Hardware' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'miscellaneous', label: 'Miscellaneous' },
  { value: 'other', label: 'Other' }
];

// One stable colour per category so the donut, legend and list dots agree.
const CATEGORY_COLORS = {
  office_supplies: '#7C5CFC',
  utilities: '#0EA5E9',
  rent: '#F97316',
  salaries: '#16A34A',
  marketing: '#F5A524',
  travel: '#4C6FFF',
  software: '#8B5CF6',
  hardware: '#EC4899',
  maintenance: '#14B8A6',
  miscellaneous: '#64748B',
  other: '#94A3B8',
};
const FALLBACK_COLORS = ['#4C6FFF', '#16A34A', '#F5A524', '#7C5CFC', '#EC4899', '#0EA5E9'];
const categoryColor = (key, i = 0) => CATEGORY_COLORS[key] || FALLBACK_COLORS[i % FALLBACK_COLORS.length];

// Month helpers — the chip row and the stats query share this range shape.
const monthRange = (d) => {
  const start = new Date(d.getFullYear(), d.getMonth(), 1);
  const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const iso = (x) => x.toISOString().split('T')[0];
  return {
    value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
    label: d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
    startDate: iso(start),
    endDate: iso(end),
  };
};

const MONTH_OPTIONS = Array.from({ length: 6 }, (_, i) => {
  const now = new Date();
  return monthRange(new Date(now.getFullYear(), now.getMonth() - (5 - i), 1));
});

const currentMonthFilter = () => {
  const m = MONTH_OPTIONS[MONTH_OPTIONS.length - 1];
  return { startDate: m.startDate, endDate: m.endDate };
};

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash', icon: '💵' },
  { value: 'upi', label: 'UPI', icon: '📱' },
  { value: 'bank_transfer', label: 'Bank Transfer', icon: '🏦' },
  { value: 'credit_card', label: 'Credit Card', icon: '💳' },
  { value: 'debit_card', label: 'Debit Card', icon: '💳' },
  { value: 'cheque', label: 'Cheque', icon: '📝' },
  { value: 'other', label: 'Other', icon: '📄' }
];

const ExpendituresPage = () => {
  const { user } = useAuth();
  // DigiHR platform_staff only see the actions their platformPermissions grant;
  // superadmin (and any other role that can reach this page) keeps full access.
  const canManage = (action) => user?.role !== 'platform_staff' || user?.platformPermissions?.expenditures?.[action] === true;
  const [expenditures, setExpenditures] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    paymentMethod: '',
    client: '',
    ...currentMonthFilter(),
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingExpenditure, setEditingExpenditure] = useState(null);
  const [viewingExpenditure, setViewingExpenditure] = useState(null);
  
  // Stats
  const [stats, setStats] = useState(null);
  
  // Clients for dropdown
  const { data: clientsData } = useApi(clientAPI.getClients, []);
  const clients = clientsData?.clients || [];
  
  // Form state
  const [form, setForm] = useState({
    title: '',
    description: '',
    amount: '',
    category: 'miscellaneous',
    paymentMethod: 'cash',
    upiReferenceId: '',
    bankTransactionId: '',
    chequeNumber: '',
    date: new Date().toISOString().split('T')[0],
    vendorName: '',
    vendorContact: '',
    client: '',
    notes: '',
    tags: ''
  });
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const fileInputRef = useRef(null);
  
  const { execute, loading: actionLoading } = useAsyncAction();

  useEffect(() => {
    fetchExpenditures();
  }, [pagination.page, filters]);

  // Breakdown is always visible, and always scoped to the selected date range.
  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.startDate, filters.endDate]);

  const activeMonth = MONTH_OPTIONS.find((m) => m.startDate === filters.startDate)?.value || '';
  const activeMonthLabel =
    MONTH_OPTIONS.find((m) => m.value === activeMonth)?.label || 'Selected range';

  const selectMonth = (value) => {
    const m = MONTH_OPTIONS.find((o) => o.value === value);
    if (!m) return;
    setPagination((p) => ({ ...p, page: 1 }));
    setFilters((f) => ({ ...f, startDate: m.startDate, endDate: m.endDate }));
  };

  const fetchExpenditures = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      };
      const response = await expenditureAPI.getExpenditures(params);
      if (response.status === 'success') {
        setExpenditures(response.data.expenditures || []);
        setPagination(response.data.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch expenditures');
      setExpenditures([]); // Ensure expenditures is always an array even on error
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await expenditureAPI.getStats({
        startDate: filters.startDate,
        endDate: filters.endDate,
      });
      if (response.status === 'success') {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setReceiptFile(file);
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => setReceiptPreview(reader.result);
        reader.readAsDataURL(file);
      } else {
        setReceiptPreview(null);
      }
    }
  };

  const resetForm = () => {
    setForm({
      title: '',
      description: '',
      amount: '',
      category: 'miscellaneous',
      paymentMethod: 'cash',
      upiReferenceId: '',
      bankTransactionId: '',
      chequeNumber: '',
      date: new Date().toISOString().split('T')[0],
      vendorName: '',
      vendorContact: '',
      client: '',
      notes: '',
      tags: ''
    });
    setReceiptFile(null);
    setReceiptPreview(null);
    setEditingExpenditure(null);
  };

  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const openEditModal = (exp) => {
    setEditingExpenditure(exp);
    setForm({
      title: exp.title || '',
      description: exp.description || '',
      amount: exp.amount?.toString() || '',
      category: exp.category || 'miscellaneous',
      paymentMethod: exp.paymentMethod || 'cash',
      upiReferenceId: exp.upiReferenceId || '',
      bankTransactionId: exp.bankTransactionId || '',
      chequeNumber: exp.chequeNumber || '',
      date: exp.date ? new Date(exp.date).toISOString().split('T')[0] : '',
      vendorName: exp.vendor?.name || '',
      vendorContact: exp.vendor?.contact || '',
      client: exp.client?._id || '',
      notes: exp.notes || '',
      tags: exp.tags?.join(', ') || ''
    });
    setReceiptPreview(exp.receipt?.url || null);
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }
    if (!form.amount || parseFloat(form.amount) <= 0) {
      setError('Valid amount is required');
      return;
    }
    
    const formData = new FormData();
    formData.append('title', form.title.trim());
    formData.append('description', form.description.trim());
    formData.append('amount', form.amount);
    formData.append('category', form.category);
    formData.append('paymentMethod', form.paymentMethod);
    formData.append('date', form.date);
    
    if (form.paymentMethod === 'upi' && form.upiReferenceId) {
      formData.append('upiReferenceId', form.upiReferenceId);
    }
    if (form.paymentMethod === 'bank_transfer' && form.bankTransactionId) {
      formData.append('bankTransactionId', form.bankTransactionId);
    }
    if (form.paymentMethod === 'cheque' && form.chequeNumber) {
      formData.append('chequeNumber', form.chequeNumber);
    }
    
    if (form.vendorName || form.vendorContact) {
      formData.append('vendor', JSON.stringify({ name: form.vendorName, contact: form.vendorContact }));
    }
    if (form.client) {
      formData.append('client', form.client);
    }
    if (form.notes) {
      formData.append('notes', form.notes);
    }
    if (form.tags) {
      formData.append('tags', JSON.stringify(form.tags.split(',').map(t => t.trim()).filter(Boolean)));
    }
    if (receiptFile) {
      formData.append('receipt', receiptFile);
    }
    
    const result = await execute(
      editingExpenditure 
        ? () => expenditureAPI.updateExpenditure(editingExpenditure._id, formData)
        : () => expenditureAPI.createExpenditure(formData)
    );
    
    if (result.success) {
      setSuccess(editingExpenditure ? 'Expenditure updated successfully' : 'Expenditure created successfully');
      setIsModalOpen(false);
      resetForm();
      fetchExpenditures();
      fetchStats();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to save expenditure');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this expenditure?')) return;
    
    const result = await execute(() => expenditureAPI.deleteExpenditure(id));
    if (result.success) {
      setSuccess('Expenditure deleted successfully');
      fetchExpenditures();
      fetchStats();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to delete expenditure');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getPaymentMethodLabel = (method) => {
    const pm = PAYMENT_METHODS.find(p => p.value === method);
    return pm ? `${pm.icon} ${pm.label}` : method;
  };

  const getCategoryLabel = (cat) => {
    const c = CATEGORIES.find(c => c.value === cat);
    return c ? c.label : cat;
  };

  const donutData = (stats?.byCategory || []).map((c, i) => ({
    name: getCategoryLabel(c._id),
    value: c.total,
    color: categoryColor(c._id, i),
  }));

  const largestCategory = stats?.byCategory?.[0];
  const vendorCount = new Set(
    (expenditures || []).map((e) => e.vendor?.name).filter(Boolean)
  ).size;

  return (
    <div style={{ fontFamily: FONT_UI }}>
      <PageHeader
        title="Expenditures"
        subtitle="Platform operating costs — infrastructure, salaries, tools, travel and more"
        actions={
          canManage('create') && (
            <PrimaryButton icon={Plus} onClick={openCreateModal}>
              Add expense
            </PrimaryButton>
          )
        }
      />

      <Banner tone="error" message={error} onClose={() => setError('')} />
      <Banner tone="success" message={success} onClose={() => setSuccess('')} />

      {/* Month picker — drives both the list and the breakdown below */}
      <div className="mb-5">
        <ChipTabs options={MONTH_OPTIONS} value={activeMonth} onChange={selectMonth} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <StatCard
          label={`${activeMonthLabel} total`}
          value={formatCurrency(stats?.total || 0)}
          hint={`${stats?.count || 0} transactions`}
        />
        <StatCard
          label="Largest category"
          value={largestCategory ? getCategoryLabel(largestCategory._id) : '—'}
          hint={largestCategory ? formatCurrency(largestCategory.total) : 'No spend recorded'}
        />
        <StatCard label="Vendors" value={fmtNum(vendorCount)} hint="distinct vendors this page" />
        <StatCard
          label="Top payment method"
          value={stats?.byPaymentMethod?.[0] ? getPaymentMethodLabel(stats.byPaymentMethod[0]._id) : '—'}
          hint={stats?.byPaymentMethod?.[0] ? formatCurrency(stats.byPaymentMethod[0].total) : '—'}
        />
      </div>

      <Toolbar
        value={filters.search}
        onChange={(v) => setFilters({ ...filters, search: v })}
        placeholder="Search by title, description, vendor..."
      >
        <GhostButton icon={Filter} onClick={() => setShowFilters(!showFilters)}>
          Filters
        </GhostButton>
        <GhostButton
          icon={RefreshCw}
          onClick={() => setFilters({ ...currentMonthFilter(), search: '', category: '', paymentMethod: '', client: '' })}
        >
          Reset
        </GhostButton>
      </Toolbar>

      {showFilters && (
        <Card className="p-4 mb-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              className="px-3 py-2.5 rounded-lg text-sm outline-none"
              style={{ border: `1px solid ${C.border}`, color: C.ink }}
            >
              <option value="">All categories</option>
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <select
              value={filters.paymentMethod}
              onChange={(e) => setFilters({ ...filters, paymentMethod: e.target.value })}
              className="px-3 py-2.5 rounded-lg text-sm outline-none"
              style={{ border: `1px solid ${C.border}`, color: C.ink }}
            >
              <option value="">All payment methods</option>
              {PAYMENT_METHODS.map((pm) => (
                <option key={pm.value} value={pm.value}>{pm.label}</option>
              ))}
            </select>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="px-3 py-2.5 rounded-lg text-sm outline-none"
              style={{ border: `1px solid ${C.border}`, color: C.ink, fontFamily: FONT_DATA }}
            />
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="px-3 py-2.5 rounded-lg text-sm outline-none"
              style={{ border: `1px solid ${C.border}`, color: C.ink, fontFamily: FONT_DATA }}
            />
            <select
              value={filters.client}
              onChange={(e) => setFilters({ ...filters, client: e.target.value })}
              className="px-3 py-2.5 rounded-lg text-sm outline-none"
              style={{ border: `1px solid ${C.border}`, color: C.ink }}
            >
              <option value="">All clients</option>
              {clients.map((c) => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* By category */}
        <SectionCard title="By category" bodyClassName="p-5">
          {donutData.length ? (
            <>
              <div className="flex justify-center">
                <Donut data={donutData} />
              </div>
              <div className="mt-5 space-y-2">
                {donutData.map((d) => (
                  <div key={d.name} className="flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-2 text-[12px]" style={{ color: C.textMuted }}>
                      <span className="w-2 h-2 rounded-full" style={{ background: d.color }} />
                      {d.name}
                    </span>
                    <Mono className="text-[12px]" style={{ color: C.ink }}>
                      {formatCurrency(d.value)}
                    </Mono>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-[12px] text-center py-10" style={{ color: C.textMuted }}>
              No spend recorded for {activeMonthLabel}.
            </p>
          )}
        </SectionCard>

        {/* Expense list */}
        <Card className="overflow-hidden xl:col-span-2">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <LoadingSpinner size="lg" />
            </div>
          ) : !expenditures || expenditures.length === 0 ? (
            <EmptyState
              icon={Receipt}
              title="No expenditures found"
              message={`Nothing recorded for ${activeMonthLabel} with these filters.`}
              action={
                canManage('create') && (
                  <PrimaryButton icon={Plus} onClick={openCreateModal}>
                    Add expense
                  </PrimaryButton>
                )
              }
            />
          ) : (
            <>
              <Table minWidth={720}>
                <thead>
                  <tr>
                    <TH>Expense</TH>
                    <TH>Category</TH>
                    <TH align="right">Amount</TH>
                    <TH>Paid via</TH>
                    <TH align="center">Receipt</TH>
                    <TH align="right">Actions</TH>
                  </tr>
                </thead>
                <tbody>
                  {(expenditures || []).map((exp) => (
                    <tr key={exp._id} className="hover:bg-slate-50">
                      <TD>
                        <span className="block font-medium">{exp.title}</span>
                        <Mono className="block text-[11px] mt-0.5" style={{ color: C.textMuted }}>
                          {[
                            exp.vendor?.name,
                            new Date(exp.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </Mono>
                        {exp.client && (
                          <span className="block text-[11px]" style={{ color: C.signal }}>
                            Client: {exp.client.name}
                          </span>
                        )}
                      </TD>
                      <TD>
                        <span className="inline-flex items-center gap-1.5 text-[12px]" style={{ color: C.textMuted }}>
                          <span
                            className="w-2 h-2 rounded-full"
                            style={{ background: categoryColor(exp.category, 0) }}
                          />
                          {getCategoryLabel(exp.category)}
                        </span>
                      </TD>
                      <TD align="right">
                        <Mono className="font-semibold">{formatCurrency(exp.amount)}</Mono>
                      </TD>
                      <TD>
                        <span className="text-[12px]" style={{ color: C.textMuted }}>
                          {getPaymentMethodLabel(exp.paymentMethod)}
                        </span>
                      </TD>
                      <TD align="center">
                        {exp.receipt?.url ? (
                          <a
                            href={exp.receipt.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg"
                            style={{ background: '#ECFDF5', color: '#047857' }}
                            title="Open receipt"
                          >
                            <Image className="w-4 h-4" />
                          </a>
                        ) : (
                          <span style={{ color: C.textFaint }}>—</span>
                        )}
                      </TD>
                      <TD align="right">
                        <div className="flex justify-end gap-1">
                          <IconButton icon={Eye} title="View" onClick={() => setViewingExpenditure(exp)} />
                          {canManage('update') && (
                            <IconButton icon={Pencil} title="Edit" onClick={() => openEditModal(exp)} />
                          )}
                          {canManage('delete') && (
                            <IconButton icon={Trash2} title="Delete" tone={C.rose} onClick={() => handleDelete(exp._id)} />
                          )}
                        </div>
                      </TD>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {pagination.pages > 1 && (
                <div
                  className="flex items-center justify-between px-5 py-3"
                  style={{ borderTop: `1px solid ${C.border}` }}
                >
                  <Mono className="text-[12px]" style={{ color: C.textMuted }}>
                    {(pagination.page - 1) * pagination.limit + 1}–
                    {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                  </Mono>
                  <div className="flex gap-2">
                    <IconButton
                      icon={ChevronLeft}
                      title="Previous page"
                      onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                      disabled={pagination.page === 1}
                    />
                    <IconButton
                      icon={ChevronRight}
                      title="Next page"
                      onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                      disabled={pagination.page === pagination.pages}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setIsModalOpen(false)} />
            <div className="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingExpenditure ? 'Edit Expenditure' : 'Add New Expenditure'}
                </h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 rounded-lg hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {/* Title & Amount */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Office Rent January"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Amount (₹) <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="number"
                        value={form.amount}
                        onChange={(e) => setForm({ ...form, amount: e.target.value })}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Category & Date */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {CATEGORIES.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <input
                      type="date"
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>

                {/* Payment Method */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Method <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {PAYMENT_METHODS.map(pm => (
                      <button
                        key={pm.value}
                        type="button"
                        onClick={() => setForm({ ...form, paymentMethod: pm.value })}
                        className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all ${
                          form.paymentMethod === pm.value
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className="text-xl">{pm.icon}</span>
                        <span className="text-xs mt-1">{pm.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Payment-specific fields */}
                {form.paymentMethod === 'upi' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      UPI Reference ID
                    </label>
                    <input
                      type="text"
                      value={form.upiReferenceId}
                      onChange={(e) => setForm({ ...form, upiReferenceId: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 123456789012"
                    />
                  </div>
                )}
                {form.paymentMethod === 'bank_transfer' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bank Transaction ID
                    </label>
                    <input
                      type="text"
                      value={form.bankTransactionId}
                      onChange={(e) => setForm({ ...form, bankTransactionId: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., TXN123456789"
                    />
                  </div>
                )}
                {form.paymentMethod === 'cheque' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cheque Number
                    </label>
                    <input
                      type="text"
                      value={form.chequeNumber}
                      onChange={(e) => setForm({ ...form, chequeNumber: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 000123"
                    />
                  </div>
                )}

                {/* Vendor Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name</label>
                    <input
                      type="text"
                      value={form.vendorName}
                      onChange={(e) => setForm({ ...form, vendorName: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., ABC Supplies"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Contact</label>
                    <input
                      type="text"
                      value={form.vendorContact}
                      onChange={(e) => setForm({ ...form, vendorContact: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Phone or Email"
                    />
                  </div>
                </div>

                {/* Client (Optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Link to Client (Optional)
                  </label>
                  <select
                    value={form.client}
                    onChange={(e) => setForm({ ...form, client: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">No Client</option>
                    {clients.map(c => (
                      <option key={c._id} value={c._id}>{c.name} ({c.code})</option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows={2}
                    placeholder="Additional details..."
                  />
                </div>

                {/* Receipt Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Receipt / Invoice
                  </label>
                  <div className="flex items-start gap-4">
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="flex-1 border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                    >
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Click to upload receipt</p>
                      <p className="text-xs text-gray-400 mt-1">JPG, PNG or PDF (max 5MB)</p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/jpg,application/pdf"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </div>
                    {(receiptPreview || receiptFile) && (
                      <div className="relative">
                        {receiptPreview?.startsWith('data:image') || receiptPreview?.includes('.jpg') || receiptPreview?.includes('.png') ? (
                          <img src={receiptPreview} alt="Receipt" className="w-24 h-24 object-cover rounded-lg border" />
                        ) : (
                          <div className="w-24 h-24 flex items-center justify-center bg-gray-100 rounded-lg border">
                            <FileText className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => { setReceiptFile(null); setReceiptPreview(null); }}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                  <input
                    type="text"
                    value={form.tags}
                    onChange={(e) => setForm({ ...form, tags: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Comma-separated tags (e.g., monthly, recurring)"
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows={2}
                    placeholder="Internal notes..."
                  />
                </div>

                {/* Submit */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" loading={actionLoading}>
                    {editingExpenditure ? 'Update Expenditure' : 'Create Expenditure'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewingExpenditure && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setViewingExpenditure(null)} />
            <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Expenditure Details</h3>
                <button onClick={() => setViewingExpenditure(null)} className="p-2 rounded-lg hover:bg-gray-100">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900">{viewingExpenditure.title}</h4>
                    <p className="text-sm text-gray-500">{new Date(viewingExpenditure.date).toLocaleDateString('en-IN', { dateStyle: 'full' })}</p>
                  </div>
                  <span className="text-2xl font-bold text-red-600">{formatCurrency(viewingExpenditure.amount)}</span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Category</p>
                    <p className="font-medium">{getCategoryLabel(viewingExpenditure.category)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Payment Method</p>
                    <p className="font-medium">{getPaymentMethodLabel(viewingExpenditure.paymentMethod)}</p>
                  </div>
                  {viewingExpenditure.upiReferenceId && (
                    <div className="col-span-2">
                      <p className="text-gray-500">UPI Reference ID</p>
                      <p className="font-medium font-mono">{viewingExpenditure.upiReferenceId}</p>
                    </div>
                  )}
                  {viewingExpenditure.chequeNumber && (
                    <div className="col-span-2">
                      <p className="text-gray-500">Cheque Number</p>
                      <p className="font-medium font-mono">{viewingExpenditure.chequeNumber}</p>
                    </div>
                  )}
                  {viewingExpenditure.vendor?.name && (
                    <div>
                      <p className="text-gray-500">Vendor</p>
                      <p className="font-medium">{viewingExpenditure.vendor.name}</p>
                      {viewingExpenditure.vendor.contact && (
                        <p className="text-xs text-gray-400">{viewingExpenditure.vendor.contact}</p>
                      )}
                    </div>
                  )}
                  {viewingExpenditure.client && (
                    <div>
                      <p className="text-gray-500">Client</p>
                      <p className="font-medium">{viewingExpenditure.client.name}</p>
                    </div>
                  )}
                </div>
                
                {viewingExpenditure.description && (
                  <div>
                    <p className="text-gray-500 text-sm">Description</p>
                    <p className="text-gray-700">{viewingExpenditure.description}</p>
                  </div>
                )}
                
                {viewingExpenditure.receipt?.url && (
                  <div>
                    <p className="text-gray-500 text-sm mb-2">Receipt</p>
                    <a
                      href={viewingExpenditure.receipt.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
                    >
                      <Image className="w-4 h-4" />
                      View Receipt
                    </a>
                  </div>
                )}

                {viewingExpenditure.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {viewingExpenditure.tags.map((tag, i) => (
                      <span key={i} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpendituresPage;
